import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../store'
import { Treatment, TreatmentCategory } from '../models/types'
import { findAppointments } from 'patient-api-lib'
import { DateTime } from 'luxon'

type TreatmentSelectionState = {
  loading: boolean
  categoryTreatments: Treatment[]
  selectedTreatmentCategory: TreatmentCategory | null
}

export const initialState: TreatmentSelectionState = {
  loading: false,
  categoryTreatments: [],
  selectedTreatmentCategory: null,
}

export const getTreatmentsFromCategory = createAsyncThunk<
  Treatment[],
  void,
  { dispatch: AppDispatch; state: RootState }
>('treatmentScreen/getTreatmentsFromCategory', async (_, thunkApi) => {
  const { selectedTreatmentCategory } = thunkApi.getState().treatmentSelection
  const clinics = thunkApi.getState().app.clinics
  const today = DateTime.now()
  const endDate = today.plus({ month: 3 })
  const treatmentList = selectedTreatmentCategory?.treatmentsList

  const appointmentsResponse = await findAppointments(
    today.toJSDate(),
    endDate.toJSDate(),
    treatmentList?.map((t) => t.id),
    clinics.map((c) => c.id),
    '1',
    1000
  )

  const appointments = appointmentsResponse.getAppointmentsList()

  const availableTreatmentIds = appointments.map((a) => a.getTreatmentId())
  const clinicsHasTreatments = clinics.flatMap((c) => c.treatmentIds)

  const availableTreatments = treatmentList?.filter((t) => {
    const hasTreatment = clinicsHasTreatments.includes(t.id)
    const isAvailable = availableTreatmentIds.includes(t.id)
    return hasTreatment && isAvailable
  })

  return availableTreatments || []
})

export const TreatmentSelectionSlice = createSlice({
  name: 'treatmentSelectionScreen',
  initialState,
  reducers: {
    setSelectedTreatmentCategory: (
      state,
      action: PayloadAction<TreatmentCategory | null>
    ) => {
      state.selectedTreatmentCategory = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTreatmentsFromCategory.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getTreatmentsFromCategory.rejected, (state) => {
      state.loading = false
      state.categoryTreatments = []
    }),
      builder.addCase(
        getTreatmentsFromCategory.fulfilled,
        (state, action: PayloadAction<Treatment[]>) => {
          state.categoryTreatments = action.payload
          state.loading = false
        }
      )
  },
})

export const getCategoryTreatments = (state: RootState) =>
  state.treatmentSelection.categoryTreatments

export const getSelectedTreatmentCategory = (state: RootState) =>
  state.treatmentSelection.selectedTreatmentCategory

export const { setSelectedTreatmentCategory } = TreatmentSelectionSlice.actions

export default TreatmentSelectionSlice.reducer
